/**
 * 20260920 - VIEWPORT TRANSITION: "the DEVICE is changing shape right now, so nothing may glide."
 *
 * A rotation, a fold/unfold, a Split View re-balance and a plain window resize all move the viewport
 * under a layout that is already on screen. Every layout change that follows one of those is the app
 * CATCHING UP with the device, not a change the user asked to watch — so it has to SNAP. The
 * quarter-second glides the board's Motion layout elements and the shot-actions panel run in that
 * window are the owner-reported jank (20260920): an empty grey panel slot, then a second column hop,
 * a fifth of a second after the system's own rotation animation has already finished.
 *
 * WHY MOTION'S OWN GUARD IS NOT ENOUGH. motion-dom already blocks the layout update that follows a
 * window resize whose innerWidth changed (create-projection-node.mjs, updateBlockedByResize, 250ms).
 * The glides that are still visible therefore come from the layout changes that arrive WITHOUT a
 * resize of their own, or too long after one:
 *   - the native `setFoldState` push rewriting `--safe-*` (platform/foldStateStore.ts), which reaches
 *     the grid only through a ResizeObserver a task later; and
 *   - the shot-actions panel's layout gate flipping, whose exit hold releases the narrowed column
 *     count ~220ms after the flip.
 *
 * ORDER-INDEPENDENT BY DESIGN, and that is the whole reason this is a self-clearing SIGNAL rather
 * than a handler that wraps one event. Measured on the foldable simulator with the native follower
 * live: the fold-state push arrives at +115..145ms today and at about +10..30ms once the C# half
 * ships, while the page's own `resize` lands at +20..100ms on a one-scene board and at +270..290ms
 * on the 316-scene board (the main thread is simply busy; no animation frame fires in between), with
 * the grid re-measure ~120ms behind it. So the push can arrive BEFORE or AFTER the resize, and
 * neither ordering may start a glide. A flag any of them can raise answers both orders identically.
 *
 * THE QUIET PERIOD IS SIZED AGAINST THE SLOW CASE. The timer re-arms on every mark, so what it has
 * to cover is the gap from the LAST input to the last layout change: on the 316-scene board that is
 * the resize at ~+290ms to the grid re-measure at ~+420ms, i.e. 130ms. 600ms is ~4.5x that, outlasts
 * the 300ms system rotation animation end to end (a mark at t=0 covers past its finish on its own),
 * and is still short enough that a tap a beat after a rotation animates normally. Do not raise it
 * much: everything inside this window SNAPS, including changes a user did ask to watch.
 *
 * IT CANNOT LATCH. There is no "the viewport has finished changing" event to miss — WKWebView emits
 * nothing of the sort, which is the same gap foldStateStore's settle ladder exists for — so the only
 * clear is the timer, and every mark re-arms exactly one.
 */
import { useSyncExternalStore } from 'react';

/** See the header: sized against the 316-scene board's resize→grid gap, with headroom. */
export const VIEWPORT_TRANSITION_QUIET_MS = 600;

let transitioning = false;
let quietTimer: ReturnType<typeof setTimeout> | null = null;
const listeners = new Set<() => void>();

function notify(): void {
  // Copied before iterating: a subscriber that unsubscribes in its own callback (React does exactly
  // that when the flag's flip unmounts the component reading it) must not skip the next listener.
  for (const listener of [...listeners]) listener();
}

function clearTransition(): void {
  quietTimer = null;
  if (!transitioning) return;
  transitioning = false;
  notify();
}

/**
 * "Something just moved the viewport." Raises the flag if it is down and (re)arms the ONE quiet-period
 * timer that lowers it. Cheap and idempotent — a burst of fifty resize events costs one timer.
 */
export function markViewportChange(): void {
  if (quietTimer !== null) clearTimeout(quietTimer);
  quietTimer = setTimeout(clearTransition, VIEWPORT_TRANSITION_QUIET_MS);
  if (transitioning) return;
  transitioning = true;
  notify();
}

/** The press-time read, for callers that must not subscribe (see useSideDock's snapshot idiom). */
export function isViewportTransitioning(): boolean {
  return transitioning;
}

/** The store's subscribe half; named rather than bare `subscribe` so import sites say what it is. */
export function subscribeViewportTransition(callback: () => void): () => void {
  listeners.add(callback);
  return () => { listeners.delete(callback); };
}

/**
 * The React SUBSCRIPTION, for a consumer that must re-render when the window opens or closes.
 *
 * THE BOARD DELIBERATELY DOES NOT USE IT (review, 20260920) - it reads isViewportTransitioning() in its
 * render body instead. A subscription costs one full render at each end of the window, and for a real
 * `resize` the raise does NOT share a commit with the layout change: a microtask checkpoint runs between
 * browser-dispatched listeners, so this store's notify commits by itself at the head of the event. On a
 * large board that is ~60ms+ spent before the re-layout it describes has even started. Reach for this
 * hook only in something cheap that genuinely has nothing else re-rendering it.
 */
export function useViewportTransitioning(): boolean {
  return useSyncExternalStore(subscribeViewportTransition, isViewportTransitioning, () => false);
}

/**
 * The window listeners, installed ONCE from main.tsx BEFORE React mounts. Both halves of that are
 * load-bearing:
 *
 * - EARLY, so this listener is registered before any component's. Among listeners on the same target
 *   and in the same phase the DOM dispatches in REGISTRATION order, so being first is the only way to
 *   be sure the flag is already up when someone else's `resize` handler runs.
 * - `capture: true`, because `resize` is dispatched AT the window with nothing above it, and the DOM
 *   runs the capturing traversal of the path before the bubbling one even at the target. So a capture
 *   listener beats every bubble-phase `resize` listener in the app whatever order they registered in
 *   — foldStateStore's watcher, useAdaptiveGrid's, useMobileLayout's new backstop.
 *
 * Why that ordering matters at all: React 19 treats a window `resize` listener as DISCRETE priority
 * and a useSyncExternalStore update as SyncLane, so a layout hook that re-reads its media query
 * inside the resize event COMMITS BEFORE THE SAME PAINT. If the flag were not already up in that
 * commit, the commit would be the one that starts the glide, and raising the flag a line later would
 * be too late to stop it. (The MediaQueryList `change` delivery that accompanies the same resize
 * arrives after it — HTML's "update the rendering" runs the resize steps before it evaluates media
 * queries — so it is covered by the same raise.)
 *
 * Returns a disposer; main.tsx drops it on purpose, as it does for the safe-area watcher next to it.
 * Self-gates on `typeof window` so importing this module costs nothing in a headless host.
 */
export function installViewportTransitionListeners(): () => void {
  if (typeof window === 'undefined') return () => {};
  const onViewportEvent = () => markViewportChange();
  // `orientationchange` is here for the same reason it is in useRegularWidthLayout.ts and
  // foldStateStore.ts: this WebView drops MediaQueryList/ResizeObserver deliveries across a rotation,
  // so a rotation can arrive with this event and nothing else. No next-frame re-fire is needed — the
  // flag is not a measurement, and raising it before the new layout exists is exactly right.
  window.addEventListener('resize', onViewportEvent, { capture: true });
  window.addEventListener('orientationchange', onViewportEvent, { capture: true });
  return () => {
    window.removeEventListener('resize', onViewportEvent, { capture: true });
    window.removeEventListener('orientationchange', onViewportEvent, { capture: true });
  };
}

/** Test-only: drops the flag and any armed timer, so module state cannot leak between cases. */
export function __resetViewportTransitionForTests(): void {
  if (quietTimer !== null) clearTimeout(quietTimer);
  quietTimer = null;
  transitioning = false;
  notify();
}
